import { useState, useEffect } from 'react';
import alertsService from '../services/alertsService';
import { useToast } from '../contexts/ToastContext';
import './Alerts.css';

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function AlertDetailModal({ alert, onClose, onDownload }) {
  if (!alert) return null;
  return (
    <div className="response-modal" onClick={onClose}>
      <div className="response-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="response-modal-header">
          <h3>{alert.type} Details</h3>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="response-form" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="alert-header" style={{ marginBottom: '0.75rem' }}>
            <h3>{alert.type}</h3>
            <span className={`severity ${alert.severity.toLowerCase()}`}>{alert.severity}</span>
          </div>
          <span className="alert-location-text">{alert.location}</span>
          <p className="alert-description">{alert.description}</p>

          <div className="alert-times">
            <span className="alert-time">Issued: {formatDate(alert.issueDate)}</span>
            <span className="alert-time">Closed: {formatDate(alert.closed_at)}</span>
          </div>
          <div className="close-reason">
            <strong>Reason:</strong> {alert.closed_reason || 'No reason provided'}
          </div>

          {((alert.neededEquipment ?? []).length > 0 || (alert.neededSkills ?? []).length > 0) && (
            <div className="alert-needs" style={{ marginTop: '1rem' }}>
              {(alert.neededEquipment ?? []).map(item => (
                <span key={`eq-${item.id}`} className="need-tag need-eq">{item.label}</span>
              ))}
              {(alert.neededSkills ?? []).map(item => (
                <span key={`sk-${item.id}`} className="need-tag need-sk">{item.label}</span>
              ))}
            </div>
          )}

          {(alert.responders ?? []).length > 0 && (
            <div className="responders-section">
              <h4>Responding Helpers ({alert.responders.length})</h4>
              <div className="responders-list">
                {alert.responders.map((responder, idx) => (
                  <div key={idx} className="responder-card">
                    <div className="responder-header">
                      <span className="responder-name">{responder.name}</span>
                      {responder.eta && <span className="responder-eta">{responder.eta}</span>}
                    </div>
                    {responder.message && <p className="responder-message">{responder.message}</p>}
                    <div className="responder-chips">
                      {(responder.equipment ?? []).map(id => {
                        const item = alert.neededEquipment?.find(e => e.id === id);
                        return item ? <span key={`eq-${id}`} className="need-tag need-eq">{item.label}</span> : null;
                      })}
                      {(responder.skills ?? []).map(id => {
                        const item = alert.neededSkills?.find(s => s.id === id);
                        return item ? <span key={`sk-${id}`} className="need-tag need-sk">{item.label}</span> : null;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(alert.messages ?? []).length > 0 && (
            <div className="messages-section">
              <h4>Chat Transcript ({alert.messages.length})</h4>
              <div className="messages-list">
                {alert.messages.map((m, idx) => (
                  <div key={idx} className="message-card">
                    <div className="message-header">
                      <span className="message-author">{m.name}</span>
                      <span className="message-time">{formatDate(m.created_at)}</span>
                    </div>
                    <p className="message-text">{m.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            className="btn btn-primary"
            style={{ marginTop: '1rem', width: '100%' }}
            onClick={() => onDownload(alert)}
          >
            <i className="fas fa-file-pdf" style={{ marginRight: '0.5rem' }}></i>
            Download ICS-214 PDF
          </button>
        </div>
      </div>
    </div>
  );
}

function DepartmentAlertLog() {
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [result, setResult] = useState({ alerts: [], total: 0, total_pages: 1 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const data = await alertsService.getDepartmentAlertLog(page, perPage);
        if (!cancelled) setResult(data);
      } catch {
        if (!cancelled) addToast('Could not load department alert log.', 'error');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [page, perPage, addToast]);

  const handleDownload = async (alert) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await alertsService.downloadICS214PDF(alert.id, `ics214-alert-${alert.id}.pdf`);
      addToast('ICS-214 PDF downloaded.', 'success');
    } catch (err) {
      addToast(err.message || 'Download failed.', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const { alerts, total, total_pages } = result;

  return (
    <div className="alerts-page">
      <div className="container">
        <h1>Department Alert Log</h1>
        <p className="alerts-empty">Closed alerts your department responded to. Tap a card for details or download an ICS-214 PDF.</p>

        {isLoading ? (
          <p className="alerts-empty">Loading…</p>
        ) : alerts.length === 0 ? (
          <p className="alerts-empty">No closed alerts yet.</p>
        ) : (
          <>
            <div className="alerts-list">
              {alerts.map(alert => (
                <div
                  key={alert.id}
                  className={`alert-card ${alert.status} alert-log-card`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="alert-icon" onClick={() => setSelectedAlert(alert)}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <div className="alert-content" onClick={() => setSelectedAlert(alert)}>
                    <div className="alert-header">
                      <h3 className="alert-type-link">{alert.type}</h3>
                      <span className={`severity ${alert.severity.toLowerCase()}`}>{alert.severity}</span>
                    </div>
                    <span className="alert-location-text">{alert.location}</span>
                    <p className="alert-description">{alert.description}</p>
                    <div className="alert-footer">
                      <div className="alert-times">
                        <span className="alert-time">Closed: {formatDate(alert.closed_at)}</span>
                      </div>
                      <div className="close-reason">
                        <strong>Reason:</strong> {alert.closed_reason || 'No reason provided'}
                      </div>
                    </div>
                    <div className="alert-summary-tags">
                      {alert.responders?.length > 0 && (
                        <span className="summary-tag">{alert.responders.length} responder{alert.responders.length === 1 ? '' : 's'}</span>
                      )}
                      {alert.messages?.length > 0 && (
                        <span className="summary-tag">{alert.messages.length} message{alert.messages.length === 1 ? '' : 's'}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0.75rem' }}>
                    <button
                      className="btn btn-secondary"
                      title="Download ICS-214 PDF"
                      onClick={(e) => { e.stopPropagation(); handleDownload(alert); }}
                      disabled={isDownloading}
                    >
                      <i className="fas fa-file-pdf"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {total_pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </button>
                <span>Page {page} of {total_pages} ({total} total)</span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => Math.min(total_pages, p + 1))}
                  disabled={page >= total_pages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        <AlertDetailModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onDownload={handleDownload}
        />
      </div>
    </div>
  );
}

export default DepartmentAlertLog;
